import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Edit, Trash2, Sparkles, Map, Users, ListChecks, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { PageHero, Section } from "@/components/AnalyticsCommon";

export default function OnboardingTemplatesPage() {
  const { companyParam, companies } = useCompany();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      const { data } = await api.get("/onboarding/templates", { params });
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyParam]);

  const seedDefaults = async () => {
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      const { data } = await api.post("/onboarding/templates/seed-defaults", null, { params });
      toast.success(`${data.created || data.items?.length || 0} trilha(s) prontas.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5 pb-10" data-testid="onboarding-templates-page">
      <PageHero
        kicker={<><Map className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Performance & Analytics</>}
        title="Modelos de Trilha"
        description="Configure trilhas de onboarding por empresa, setor, cargo, perfil e turno. Cada trilha possui checklist 30/60/90 dias com etapas obrigatórias, evidências e aprovações."
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={seedDefaults} className="h-10"
                    data-testid="onb-tpl-seed">
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Trilhas padrão
            </Button>
            <NewTemplateDialog companies={companies} onSaved={load} editingId={editingId}
                               onClose={() => setEditingId(null)} />
          </div>
        }
      />

      <Section kicker="Catálogo" title={`Trilhas cadastradas (${list.length})`} testId="onb-tpl-list">
        {loading ? (
          <Skeleton className="h-[300px]" />
        ) : list.length === 0 ? (
          <div className="text-center py-10">
            <Map className="h-10 w-10 mx-auto text-[#C5A059] mb-2" />
            <p className="text-[13px] text-muted-foreground mb-3">Nenhuma trilha cadastrada.</p>
            <Button onClick={seedDefaults} size="sm" className="bg-[#234935] text-white">
              Criar trilhas padrão
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {list.map((t) => <TemplateCard key={t.id} template={t} onChange={load} onEdit={() => setEditingId(t.id)} />)}
          </div>
        )}
      </Section>
    </div>
  );
}

function TemplateCard({ template, onChange, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const stepsByPeriod = useMemo(() => {
    const g = { 30: [], 60: [], 90: [] };
    (template.steps || []).forEach((s) => g[s.periodo]?.push(s));
    return g;
  }, [template.steps]);

  const remove = async () => {
    if (!window.confirm(`Excluir trilha "${template.name}"?`)) return;
    try {
      await api.delete(`/onboarding/templates/${template.id}`);
      toast.success("Trilha excluída.");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const profileColor = {
    operacional: "bg-amber-50 text-amber-800 border-amber-200",
    administrativo: "bg-sky-50 text-sky-700 border-sky-200",
    lideranca: "bg-emerald-50 text-emerald-700 border-emerald-200",
  }[template.target_profile] || "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={`onb-tpl-card-${template.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-display text-[14.5px] font-semibold truncate">{template.name}</div>
          {template.descricao && (
            <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{template.descricao}</p>
          )}
        </div>
        <Badge variant="outline" className={`text-[9px] capitalize ${profileColor}`}>
          {template.target_profile || "Geral"}
        </Badge>
      </div>

      <div className="flex items-center gap-3 text-[10.5px] text-muted-foreground mb-2">
        <span className="flex items-center gap-1"><ListChecks className="h-3 w-3" /> {(template.steps || []).length} etapas</span>
        <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {template.assignments_count || 0} atribuições</span>
        <span>· {template.duracao_dias}d</span>
        {template.status === "inativo" && <Badge variant="outline" className="text-[9px]">Inativa</Badge>}
      </div>

      <div className="grid grid-cols-3 gap-1 my-2">
        {[30, 60, 90].map((p) => (
          <div key={p} className="text-center rounded-md bg-slate-50 p-1.5">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">D{p}</div>
            <div className="text-[12px] font-semibold tabular-nums">{stepsByPeriod[p].length}</div>
          </div>
        ))}
      </div>

      <button onClick={() => setExpanded((e) => !e)}
              className="w-full text-left text-[11px] text-[#234935] hover:underline flex items-center gap-1 my-2"
              data-testid={`onb-tpl-expand-${template.id}`}>
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {expanded ? "Ocultar" : "Ver"} etapas
      </button>

      {expanded && (
        <div className="space-y-2 mt-2">
          {[30, 60, 90].map((p) => (
            <div key={p}>
              <div className="text-[10px] uppercase tracking-wider text-[#8A6B2B] font-semibold mb-1">{p} dias</div>
              <ul className="space-y-0.5 text-[11px]">
                {stepsByPeriod[p].map((s) => (
                  <li key={s.id} className="flex items-start gap-1.5">
                    <span className="text-[#C5A059] mt-0.5">›</span>
                    <span className="flex-1">{s.titulo}</span>
                    {s.obrigatoria && <Badge variant="outline" className="text-[8.5px] px-1 py-0">Obr.</Badge>}
                    {s.aprovacao_gestor && <Badge variant="outline" className="text-[8.5px] px-1 py-0 bg-blue-50">G</Badge>}
                    {s.aprovacao_rh && <Badge variant="outline" className="text-[8.5px] px-1 py-0 bg-purple-50">RH</Badge>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1.5 pt-2 border-t border-slate-100 mt-2">
        <Button size="sm" variant="outline" onClick={onEdit} data-testid={`onb-tpl-edit-${template.id}`}>
          <Edit className="h-3 w-3 mr-1" /> Editar
        </Button>
        <Button size="sm" variant="ghost" onClick={remove}
                className="text-rose-600 hover:bg-rose-50 ml-auto"
                data-testid={`onb-tpl-del-${template.id}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function NewTemplateDialog({ companies, onSaved, editingId, onClose }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", descricao: "", publico_alvo: "", company_id: "all",
    target_profile: "operacional", duracao_dias: 90, status: "ativo",
  });
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingId) {
      setOpen(true);
      api.get(`/onboarding/templates/${editingId}`).then(({ data }) => {
        setForm({
          name: data.name || "", descricao: data.descricao || "", publico_alvo: data.publico_alvo || "",
          company_id: data.company_id || "all",
          target_profile: data.target_profile || "operacional",
          duracao_dias: data.duracao_dias || 90, status: data.status || "ativo",
        });
        setSteps(data.steps || []);
      });
    }
  }, [editingId]);

  const reset = () => {
    setForm({
      name: "", descricao: "", publico_alvo: "", company_id: "all",
      target_profile: "operacional", duracao_dias: 90, status: "ativo",
    });
    setSteps([]);
    onClose && onClose();
  };

  const addStep = (periodo) => {
    setSteps([...steps, {
      id: `tmp-${Date.now()}`, periodo, ordem: steps.length + 1,
      titulo: "Nova etapa", descricao: "", obrigatoria: true, prazo_dias: periodo,
      evidencia_obrigatoria: false, aprovacao_gestor: false, aprovacao_rh: false,
    }]);
  };

  const updateStep = (id, patch) => setSteps(steps.map((s) => s.id === id ? { ...s, ...patch } : s));
  const removeStep = (id) => setSteps(steps.filter((s) => s.id !== id));

  const save = async () => {
    if (!form.name) { toast.error("Informe o nome"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        company_id: form.company_id === "all" ? null : form.company_id,
        duracao_dias: Number(form.duracao_dias),
        steps,
      };
      if (editingId) {
        await api.put(`/onboarding/templates/${editingId}`, payload);
        toast.success("Trilha atualizada.");
      } else {
        await api.post("/onboarding/templates", payload);
        toast.success("Trilha criada.");
      }
      setOpen(false);
      reset();
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-10 bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                data-testid="onb-tpl-new-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Nova trilha
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[820px] max-h-[92vh] overflow-y-auto" aria-describedby="onb-new-desc"
                     data-testid="onb-tpl-dialog">
        <DialogHeader>
          <DialogTitle>{editingId ? "Editar trilha" : "Nova trilha de onboarding"}</DialogTitle>
        </DialogHeader>
        <p id="onb-new-desc" className="sr-only">Formulário de criação de trilha de onboarding</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider">Nome *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="onb-tpl-name" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
            <Textarea rows={2} value={form.descricao}
                      onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Empresa</Label>
            <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
              <SelectTrigger data-testid="onb-tpl-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas (genérica)</SelectItem>
                {(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Perfil alvo</Label>
            <Select value={form.target_profile} onValueChange={(v) => setForm({ ...form, target_profile: v })}>
              <SelectTrigger data-testid="onb-tpl-profile"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="operacional">Operacional</SelectItem>
                <SelectItem value="administrativo">Administrativo</SelectItem>
                <SelectItem value="lideranca">Liderança</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Duração (dias)</Label>
            <Input type="number" value={form.duracao_dias}
                   onChange={(e) => setForm({ ...form, duracao_dias: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ativo">Ativa</SelectItem>
                <SelectItem value="inativo">Inativa</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-display text-[13px] font-semibold">Etapas ({steps.length})</h4>
            <div className="flex items-center gap-1">
              {[30, 60, 90].map((p) => (
                <Button key={p} size="sm" variant="outline" onClick={() => addStep(p)}
                        data-testid={`onb-add-step-${p}`}>
                  + D{p}
                </Button>
              ))}
            </div>
          </div>
          {steps.length === 0 ? (
            <p className="text-[11px] text-muted-foreground italic py-3 text-center">
              Sem etapas — se vazio ao salvar, será preenchido com checklist padrão do perfil.
            </p>
          ) : (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {[30, 60, 90].map((p) => {
                const ps = steps.filter((s) => s.periodo === p);
                if (ps.length === 0) return null;
                return (
                  <div key={p}>
                    <div className="text-[9px] uppercase tracking-wider text-[#8A6B2B] font-semibold mt-2 mb-1">{p} dias</div>
                    {ps.map((s) => (
                      <div key={s.id} className="flex items-center gap-2 rounded-md border border-border bg-slate-50 p-2">
                        <Input value={s.titulo} className="h-7 text-[12px] flex-1"
                               onChange={(e) => updateStep(s.id, { titulo: e.target.value })}
                               data-testid={`onb-step-title-${s.id}`} />
                        <label className="flex items-center gap-1 text-[10px]">
                          <input type="checkbox" checked={s.obrigatoria}
                                 onChange={(e) => updateStep(s.id, { obrigatoria: e.target.checked })} />
                          Obr.
                        </label>
                        <label className="flex items-center gap-1 text-[10px]">
                          <input type="checkbox" checked={s.aprovacao_gestor}
                                 onChange={(e) => updateStep(s.id, { aprovacao_gestor: e.target.checked })} />
                          G
                        </label>
                        <label className="flex items-center gap-1 text-[10px]">
                          <input type="checkbox" checked={s.aprovacao_rh}
                                 onChange={(e) => updateStep(s.id, { aprovacao_rh: e.target.checked })} />
                          RH
                        </label>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-600"
                                onClick={() => removeStep(s.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}
                  className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                  data-testid="onb-tpl-save">
            {saving ? "Salvando..." : (editingId ? "Atualizar trilha" : "Criar trilha")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
